import * as React from "react";
import { IconBaseProps } from "react-icons";
import { AlertCircle } from "lucide-react";

import { cn } from "../lib/utils";
import { Label } from "./ui/label";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: React.ComponentType<IconBaseProps>;
  error?: string;
  title?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = "text", title, icon: Icon, error, required, name, ...props }, ref) => {
    const inputId = name || `input-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className="space-y-2">
        {title && (
          <Label
            htmlFor={inputId}
            className={cn(
              "text-sm font-medium text-muted-foreground",
              error && "text-red-600 dark:text-red-400"
            )}
          >
            {title}
            {required && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
          </Label>
        )}
        <div className="relative">
          {Icon && (
            <Icon
              className={cn(
                "absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2",
                error
                  ? "text-red-400"
                  : props.disabled
                  ? "text-muted-foreground"
                  : "text-primary"
              )}
              size={16}
            />
          )}
          <input
            id={inputId}
            name={name}
            type={type}
            className={cn(
              "flex h-10 w-full rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              Icon && "pl-10",
              error && "border-red-300 dark:border-red-800 focus-visible:ring-red-500",
              className
            )}
            ref={ref}
            required={required}
            {...props}
          />
        </div>
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
